import * as React from 'react'
import {mmap} from '@freckle/maybe'
import {getCountryByIso3} from '@frontrow/console/src/entities/helpers/countries'
import {H1} from '@frontrow/console/src/components/typography'
import {Link} from '@frontrow/console/src/components/ui/link'

import {faFileCsv} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {type ColumnDef} from '@tanstack/react-table'

import {
  NumberFilter,
  CountryFilter,
  YesNoFilter
} from '@frontrow/console/src/components/common/data-table/filters'
import DataTable, {
  type FilterT,
  type FetchDataParamsT,
  type DataTableColumnMeta
} from '@frontrow/console/src/components/common/data-table'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import DistrictUpdateButtonModal from '@frontrow/console/src/components/pages/districts-page/district-update-button-modal'
import DistrictCreateButtonModal from '@frontrow/console/src/components/pages/districts-page/district-create-button-modal'
import {DistrictDeleteButtonModal} from '@frontrow/console/src/components/pages/districts-page/district-delete-button-modal'
import {type DistrictT, type BasicDistrictT} from '@frontrow/console/src/models/district'
import {Routes} from '@frontrow/console/src/routers/routes'
import Restricted from '@frontrow/console/src/components/common/restricted'

type Props = {
  defaultFiltered: Array<FilterT>
  districtsData: Array<DistrictT>
  fetchData: (fetchDataParams: FetchDataParamsT) => void
  updateDistrict: (
    districtId: number,
    updatedDistrictData: BasicDistrictT,
    clearClassLinkOneRosterToken: boolean
  ) => Promise<unknown>
  createDistrict: (districtData: BasicDistrictT) => Promise<unknown>
  isLoading: boolean
  error: string | undefined | null
  deleteCleverAndClasslinkAssociations: (districtId: number) => void
}

export function DistrictsDataTable(props: Props): React.ReactElement {
  const {
    fetchData,
    districtsData,
    isLoading,
    error,
    createDistrict,
    defaultFiltered,
    updateDistrict,
    deleteCleverAndClasslinkAssociations
  } = props

  const columns: ColumnDef<DistrictT, unknown>[] = [
    {
      id: 'district.id',
      header: 'District Id',
      accessorFn: district => district.id,
      meta: {Filter: NumberFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'district.name',
      header: 'Name',
      accessorFn: district => district.name
    },
    {
      id: 'district.city',
      header: 'City',
      accessorFn: district => district.city
    },
    {
      id: 'district.administrativeArea',
      header: 'Administrative Area',
      accessorFn: district => district.administrativeArea,
      maxSize: 90
    },
    {
      id: 'district.postalCode',
      header: 'Postal Code',
      accessorFn: district => district.postalCode,
      maxSize: 90
    },
    {
      id: 'district.countryCode',
      header: 'Country',
      accessorFn: district =>
        mmap(cc => mmap(c => c.country, getCountryByIso3(cc)), district.countryCode),
      meta: {Filter: CountryFilter} satisfies DataTableColumnMeta
    },
    {
      id: 'district.active',
      header: 'Active',
      accessorFn: district => (district.active ? 'Yes' : 'No'),
      meta: {Filter: YesNoFilter} satisfies DataTableColumnMeta,
      maxSize: 60
    },
    {
      id: 'district.crmId',
      header: 'CRM Id',
      accessorFn: district => district.crmId
    },
    {
      id: 'action',
      header: 'Action',
      accessorFn: () => null,
      cell: ctx => {
        const district = ctx.row.original
        return (
          <div className="space-x-1">
            <DistrictUpdateButtonModal
              districtToUpdate={district}
              updateDistrict={updateDistrict}
              deleteCleverAndClasslinkAssociations={deleteCleverAndClasslinkAssociations}
            />
            <DistrictDeleteButtonModal districtToDelete={district} />
            <Link
              title="Update District Licenses"
              href={Routes.districts.districtLicenses(district.id)}
              state={{filters: defaultFiltered}}
              reactRouter
            >
              <i className="fa fa-list fa-lg" />
            </Link>
            <Restricted to="engineering">
              <Link
                title="Download RGP CSVs"
                href={Routes.districts.rgpCsvs(district.id)}
                state={{filters: defaultFiltered}}
                reactRouter
              >
                <FontAwesomeIcon icon={faFileCsv} size="lg" />
              </Link>
            </Restricted>
          </div>
        )
      },
      enableColumnFilter: false,
      enableSorting: false
    }
  ]

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null
  return (
    <div>
      <H1>Districts</H1>
      <DistrictCreateButtonModal createDistrict={createDistrict} />
      {errorNode}
      <DataTable
        columns={columns}
        fetchData={fetchData}
        data={districtsData}
        isLoading={isLoading}
        filterable
        manualFiltering
        updateUrlOnFilter
        defaultFiltered={defaultFiltered}
        defaultPageSize={100}
      />
    </div>
  )
}
